import { NavLink, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutGrid, Radar, Zap, CandlestickChart, Wallet, BookOpen, Calculator, CalendarDays, Sparkles, Settings, LogOut, ShieldAlert, FlaskConical } from "lucide-react";
import AlertBell from "@/components/AlertBell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  ["/", "Dashboard", LayoutGrid], ["/scanner", "Scanner", Radar], ["/signals", "Signals", Zap], ["/charts", "Charts", CandlestickChart], ["/backtest", "Backtesting", FlaskConical],
  ["/paper", "Paper Trading", Wallet], ["/journal", "Journal", BookOpen], ["/calendar", "Calendar", CalendarDays],
  ["/ai", "AI Analysis", Sparkles], ["/calculator", "Calculator", Calculator], ["/settings", "Settings", Settings],
];

function SessionBar() {
  const [s, setS] = useState(null);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const load = () => api.get("/sessions").then((r) => setS(r.data)).catch(() => {});
    load();
    const a = setInterval(load, 60000), b = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);
  return (
    <div className="flex items-center gap-4 text-[11px] font-mono text-zinc-500" data-testid="session-bar">
      <span className="flex items-center gap-1.5" data-testid="market-status">
        <span className={`h-1.5 w-1.5 rounded-full pulse-dot ${s?.market_open ? "bg-[#059669] text-white" : "bg-[#DC2626] text-white"}`} />
        {s ? (s.market_open ? "MARKET OPEN" : "MARKET CLOSED") : "…"}
      </span>
      {s?.sessions.map((x) => (
        <span key={x.name} className={x.active ? "text-zinc-900" : "text-zinc-400"} data-testid={`session-${x.name.toLowerCase().replace(" ", "-")}`}>
          {x.name.toUpperCase()}
        </span>
      ))}
      {s?.active.includes("London/New York Overlap") && <span className="text-[#007AFF]">LDN/NY OVERLAP</span>}
      <span className="text-zinc-500">{now.toISOString().slice(11, 19)} UTC</span>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [banner, setBanner] = useState("");
  useEffect(() => { api.get("/system/public").then((r) => setBanner(r.data.maintenance_banner)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-zinc-200">
        <div className="flex items-center gap-6 px-4 lg:px-6 h-12">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <div className="h-6 w-6 bg-[#007AFF] grid place-items-center rounded-sm text-white">
              <span className="font-display font-black text-[10px] text-white">FX</span>
            </div>
            <span className="font-display font-black tracking-tight text-zinc-900">Fx Cotrader</span>
          </Link>
          <div className="hidden xl:block ml-auto"><SessionBar /></div>
          <div className="ml-auto xl:ml-4 flex items-center gap-3">
            <AlertBell />
            <span className="text-xs text-zinc-500 hidden md:inline" data-testid="nav-user-name">{user?.name}</span>
            <button onClick={async () => { await logout(); nav("/login"); }} className="text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="logout-btn" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <nav className="flex items-center gap-0.5 px-2 lg:px-4 overflow-x-auto scrollbar-thin border-t border-zinc-100">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `relative flex items-center gap-1.5 px-3 h-9 text-[12px] font-medium whitespace-nowrap transition-colors ${isActive ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-800"}`}>
              {({ isActive }) => (<>
                <Icon size={14} />{label}
                {isActive && <span className="absolute left-2 right-2 bottom-0 h-[2px] bg-[#007AFF] text-white" />}
              </>)}
            </NavLink>
          ))}
        </nav>
      </header>
      {banner && <div className="bg-[#D97706]/10 border-b border-[#D97706]/30 text-[#92400E] text-xs px-6 py-2" data-testid="maintenance-banner">{banner}</div>}
      <main className="flex-1 px-4 lg:px-6 py-6 grid-bg">{children}</main>
      <footer className="border-t border-zinc-200 px-4 lg:px-6 py-3 flex items-start gap-2 text-[11px] text-zinc-500" data-testid="risk-disclaimer-footer">
        <ShieldAlert size={14} className="shrink-0 mt-0.5 text-[#D97706]" />
        <p>Risk disclaimer: Fx Cotrader provides probabilistic market analysis and decision support only. It does not guarantee profits and is not financial advice. Forex trading carries a high level of risk and may not be suitable for all investors. Past performance and confidence scores do not guarantee future results. Market data may be delayed or simulated.</p>
      </footer>
    </div>
  );
}
